import { useDispatch, useSelector } from "react-redux";
import { Card, Col, Row } from "reactstrap";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import {
  deleteShortcutAppData,
  deleteShortcutAppDataSuccess,
  getShortcutAppData,
  getShortcutAppsList,
  updateShortcutAppData,
  updateShortcutAppDataSuccess,
} from "../../../../store/shortcutApps/action";
import CreateEditMyShortcuts from "../../../../Components/popUpComponents/CreateEditMyShortcuts";
import { faviconFromUrl } from "../../../../helpers/functions/faviconFromUrl";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { toast } from "react-toastify";
import SettingsPanel from "../../components/SettingsPanel";

export default function MyShortcutSettings() {
  const dispatch = useDispatch();
  const { shortcutAppsList } = useSelector((state) => ({
    shortcutAppsList: state.ShortcutApps.shortcutAppsList?.shortcutquick_list,
  }));

  // Task 24843 - "allow me to organise these in order so when they appear in
  // the header they are in my order". The header already sorts by the Order
  // column (shortcutquick/list/my is orderBy('Order')); it just had no UI to
  // set it. Keep a local copy so the ◀ / ▶ moves reflect instantly, then
  // persist the whole 1..n sequence in one update.
  const [orderedShortcuts, setOrderedShortcuts] = useState([]);
  useEffect(() => {
    setOrderedShortcuts(shortcutAppsList || []);
  }, [shortcutAppsList]);

  const persistShortcutOrder = (list) => {
    dispatch(
      updateShortcutAppData(
        list.map((item, idx) => ({ Id: item?.Id, Order: idx + 1 }))
      )
    );
  };

  const moveShortcut = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= orderedShortcuts.length) return;
    const next = [...orderedShortcuts];
    [next[index], next[target]] = [next[target], next[index]];
    setOrderedShortcuts(next);
    persistShortcutOrder(next);
  };

  const { updateShortcutAppDataSuccessAPI } = useSelector((state) => ({
    updateShortcutAppDataSuccessAPI:
      state.ShortcutApps.updateShortcutAppDataSuccess,
  }));
  useEffect(() => {
    if (isEmpty(updateShortcutAppDataSuccessAPI)) return;
    if (updateShortcutAppDataSuccessAPI.status === 200) {
      // Re-fetch so the saved order is authoritative (and matches the header).
      dispatch(getShortcutAppsList());
    } else {
      toast.error(
        updateShortcutAppDataSuccessAPI.message || "Could not save the order"
      );
    }
    dispatch(updateShortcutAppDataSuccess([]));
  }, [updateShortcutAppDataSuccessAPI]);


  useEffect(() => {
    dispatch(getShortcutAppsList());
  }, []);

  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [isEdit, setIsEdit] = useState(false);

  const CreateEditShortcutButton = (action, id) => {
    if (action === "create") {
      return (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            setModal(true);
            setIsEdit(false);
          }}
        >
          Create Shortcut
        </button>
      );
    }
    if (action === "edit") {
      return (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            dispatch(getShortcutAppData({ id }));
            setModal(true);
            setIsEdit(true);
          }}
        >
          Edit Shortcut
        </button>
      );
    }
  };
  const { shortcutAppDataAPI } = useSelector((state) => ({
    shortcutAppDataAPI: state.ShortcutApps.shortcutAppData?.shortcutquick_data,
  }));
  const [deleteModal, setDeleteModal] = useState(false);
  const toggleDelete = () => setDeleteModal(!deleteModal);
  const [selectedForDelete, setSelectedForDelete] = useState([]);

  const handleDeleteShortcut = () => {
    dispatch(deleteShortcutAppData(selectedForDelete));
  };

  const { deleteShortcutAppDataSuccessAPI } = useSelector((state) => ({
    deleteShortcutAppDataSuccessAPI:
      state.ShortcutApps.deleteShortcutAppDataSuccess,
  }));

  useEffect(() => {
    if (isEmpty(deleteShortcutAppDataSuccessAPI)) return;
    if (deleteShortcutAppDataSuccessAPI.status === 200) {
      toast.success(deleteShortcutAppDataSuccessAPI.message);
      dispatch(getShortcutAppsList());
      setDeleteModal(false);
      setSelectedForDelete([]);
    } else {
      toast.error(deleteShortcutAppDataSuccessAPI.message);
    }

    dispatch(deleteShortcutAppDataSuccess([]));
  }, [deleteShortcutAppDataSuccessAPI]);

  return (
    <>
      {modal && (
        <CreateEditMyShortcuts
          modal={modal}
          setModal={setModal}
          toggle={toggle}
          isEdit={isEdit}
          updateData={shortcutAppDataAPI}
        />
      )}
      {deleteModal && (
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => {
            handleDeleteShortcut();
          }}
          customText={"Are you sure you want to delete this shortcut?"}
          customButtonText={"Yes, Delete It!"}
        />
      )}

      <SettingsPanel
        title="My Shortcuts"
        description="Your personal quick-link shortcuts."
      >
        <div className="p-3 border-top-0 border-start-0 border-end-0 border-dashed border">
          <div>
            <div className="h5">Quick Shortcuts</div>
            <div>{CreateEditShortcutButton("create", null)}</div>
          </div>
          {!isEmpty(orderedShortcuts) && (
            <div className="text-muted small mb-2">
              Use the arrows to set the order they appear in the header.
            </div>
          )}
          <Row className="align-items-center">
            {!isEmpty(orderedShortcuts) ? (
              <>
                <div className="col-auto">
                  <div className="p-2">
                    <div className="row g-0">
                      {orderedShortcuts?.map((item, index) => (
                        <Col key={item?.Id ?? index} className={"p-3"}>
                          <Card>
                            {/* Task 24843 - reorder controls. ◀ moves the
                                shortcut earlier, ▶ later; disabled at the ends. */}
                            <div className="d-flex justify-content-between mb-2">
                              <button
                                className="btn btn-light btn-sm"
                                title="Move earlier"
                                disabled={index === 0}
                                onClick={() => moveShortcut(index, -1)}
                              >
                                <i className="ri-arrow-left-line"></i>
                              </button>
                              <button
                                className="btn btn-light btn-sm"
                                title="Move later"
                                disabled={index === orderedShortcuts.length - 1}
                                onClick={() => moveShortcut(index, 1)}
                              >
                                <i className="ri-arrow-right-line"></i>
                              </button>
                            </div>
                            <a
                              href={item?.LinkUrl || "/#"}
                              className="dropdown-icon-item"
                              // open new tab
                              target="_blank"
                            >
                              {item?.ImageUrl ? (
                                <img src={item?.ImageUrl} alt={item?.Title} />
                              ) : faviconFromUrl(item?.LinkUrl) ? (
                                // Task 24845 - site favicon fallback instead of
                                // the generic chain icon.
                                <img
                                  src={faviconFromUrl(item?.LinkUrl)}
                                  alt={item?.Title}
                                />
                              ) : (
                                <i className="fa-solid fa-link"></i>
                              )}
                              <span>{item?.Title}</span>
                            </a>
                            <div>
                              {CreateEditShortcutButton("edit", item?.Id)}
                            </div>
                            <div>
                              <button
                                className={"btn btn-danger btn-sm mt-2 w-100"}
                                onClick={() => {
                                  setSelectedForDelete([item?.Id]);
                                  toggleDelete();
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <div className={"h5"}>You have no quick shortcuts.</div>
                <div>{CreateEditShortcutButton("create", null)}</div>
              </div>
            )}
          </Row>
        </div>
      </SettingsPanel>
    </>
  );
}
